import React, { useEffect, useMemo, useState } from 'react';
import { getApiBase } from '../../utils/apiConfig';

// Global Regulatory Reference — owner audit 2026-09-26 (SR-351/354/356/357/358).
//  · SR-356 freshness propagates upward: stale rows ⇒ a stale page state and stale region cards.
//  · SR-357 the categorical band (LOW / MODERATE / HIGH / PROHIBITED) is the client reading; the heuristic
//    0–100 number is a reviewer detail only.
//  · SR-358 the matrix encodes ONE dimension (hue = restrictiveness band); cells are equal-size and carry
//    no positional meaning.
//  · SR-351/354 regions show composition; worst-of is a screening statistic; < 3 jurisdictions ⇒ partial.
// No client-side fallback dataset: when the API is unavailable the surface says so.

type Band = 'LOW' | 'MODERATE' | 'HIGH' | 'PROHIBITED';

interface Jurisdiction {
  id: string;
  name: string;
  code: string;
  status: string;
  riskScore: number;
  framework?: string;
  lastChanged?: string;
  pendingLegislation?: string;
  sourceUrl?: string;
  lastReviewed?: string;
  restrictiveness: Band;
  scopeLabel: string;
  reviewState: 'CURRENT' | 'REVIEW_PARTIAL' | 'STALE_REVIEW';
  reviewAgeDays: number;
  reviewVerdict?: string;
  frameworkFull?: string;
  sourceSha256?: string;
  sources?: { jurisdiction?: string; url: string; publisher?: string; sha256?: string; quote?: string }[];
  reviewNotes?: string;
  claims?: { claim_id: string; proposition: string; verdict: string; url?: string; effective?: string }[];
}

interface ReferenceState {
  state: 'CURRENT' | 'PARTIAL_REVIEW' | 'STALE_REVIEW';
  stale_rows: number;
  partial_rows?: number;
  total_rows: number;
  sla_days: number;
  last_comprehensive_review: string;
  binding: string;
  risk_object: string;
  scoring: string;
}

interface Region {
  code: string;
  name: string;
  status: string;
  statusLabel: string;
  composition: { legal: number; restricted: number; banned: number; unclear: number; total: number };
  members: { id: string; name: string; code: string; status: string; statusLabel: string }[];
  coverage: 'JURISDICTION' | 'COVERED' | 'PARTIAL_COVERAGE';
  regionState: 'JURISDICTION' | 'UNIFORM' | 'MIXED' | 'PARTIAL_COVERAGE';
  screeningWorst: string;
  reviewState: 'CURRENT' | 'REVIEW_PARTIAL' | 'STALE_REVIEW';
}

export const BAND_STYLE: Record<Band, { text: string; cell: string; label: string }> = {
  LOW: { text: 'text-[color:var(--c-hex-a3b07a)]', cell: 'border-[color:rgb(var(--c-safe-rgb)/0.6)] bg-[color:rgb(var(--c-safe-rgb)/0.15)]', label: 'Low restrictiveness' },
  MODERATE: { text: 'text-[color:var(--c-text-strong)]', cell: 'border-[color:rgb(var(--c-active-rgb)/0.5)] bg-[color:rgb(var(--c-active-rgb)/0.1)]', label: 'Moderate · evolving' },
  HIGH: { text: 'text-[color:var(--c-amber)]', cell: 'border-[color:rgb(var(--c-gold-dim-rgb)/0.6)] bg-[color:rgb(var(--c-gold-dim-rgb)/0.15)]', label: 'High restrictiveness' },
  PROHIBITED: { text: 'text-[color:var(--c-hex-d08a8a)]', cell: 'border-[color:rgb(var(--c-critical-rgb)/0.7)] bg-[color:rgb(var(--c-critical-rgb)/0.2)]', label: 'Prohibited' },
};

const BANDS: Band[] = ['LOW', 'MODERATE', 'HIGH', 'PROHIBITED'];

export function StaleChip({ days, sla }: { days?: number; sla?: number }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-[color:rgb(var(--c-gold-rgb)/0.5)] bg-[color:rgb(var(--c-gold-rgb)/0.1)] px-2 py-0.5 font-mono text-[11px] tracking-wide text-[color:var(--c-gold-light)]">
      STALE REVIEW{days != null && days >= 0 ? ` · ${days}d` : ''}{sla ? ` > ${sla}d SLA` : ''}
    </span>
  );
}

export function PartialChip() {
  return (
    <span className="inline-flex items-center rounded border border-[color:rgb(var(--c-active-rgb)/0.5)] bg-[color:rgb(var(--c-active-rgb)/0.1)] px-2 py-0.5 font-mono text-[11px] tracking-wide text-[color:var(--c-hex-c9d0d8)]" title="Primary-source review is partial for this row — non-binding until every claim is primary-verified.">
      PARTIAL REVIEW
    </span>
  );
}

function compositionText(c: Region['composition']): string {
  const p: string[] = [];
  if (c.legal) p.push(`${c.legal} regulated`);
  if (c.unclear) p.push(`${c.unclear} evolving`);
  if (c.restricted) p.push(`${c.restricted} restricted`);
  if (c.banned) p.push(`${c.banned} prohibited`);
  return p.join(' · ');
}

const GlobalRegulatoryReference: React.FC = () => {
  const [rows, setRows] = useState<Jurisdiction[]>([]);
  const [ref, setRef] = useState<ReferenceState | null>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [open, setOpen] = useState(false); // collapsed by default — keeps the asset-analysis flow intact (owner 2026-09-26)

  useEffect(() => {
    const base = getApiBase();
    Promise.all([
      fetch(`${base}/api/regulatory/jurisdictions`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${base}/api/regulatory/regions`).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([j, g]) => {
        if (j?.success && Array.isArray(j.data) && j.data.length > 0) {
          setRows(j.data);
          setRef(j.reference_state ?? null);
          setSelectedId(j.data[0].id);
          if (g?.success && Array.isArray(g.data)) setRegions(g.data);
          setState('ready');
        } else setState('unavailable');
      })
      .catch(() => setState('unavailable'));
  }, []);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => BANDS.indexOf(a.restrictiveness) - BANDS.indexOf(b.restrictiveness) || a.name.localeCompare(b.name)),
    [rows],
  );
  const selected = rows.find((r) => r.id === selectedId) ?? null;
  const pageStale = ref?.state === 'STALE_REVIEW';
  const pagePartial = ref?.state === 'PARTIAL_REVIEW';
  const pageAge = rows[0]?.reviewAgeDays;

  if (state === 'loading') return <div className="py-10 text-center text-sm text-[color:var(--c-text-soft)]">Loading regulatory reference…</div>;
  if (state === 'unavailable')
    return (
      <div className="rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-deep)] p-6 text-sm text-[color:var(--c-text-body)]">
        Global regulatory reference is unavailable. No cached or client-side substitute is shown.
      </div>
    );

  return (
    <div className="space-y-6">
      {/* Page state — scope, non-scoring, freshness (SR-356) */}
      <div className="flex flex-col gap-3 rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-deep)] px-5 py-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[color:var(--c-text)]">Reference state</span>
            <span className="rounded border border-[color:rgb(var(--c-ink-rgb)/0.14)] px-2 py-0.5 font-mono text-[11px] tracking-wide text-[color:var(--c-text-strong)]">
              {ref?.scoring ?? 'NON-SCORING · NOT ASSET-SPECIFIC'}
            </span>
            {pageStale && <StaleChip days={pageAge} sla={ref?.sla_days} />}
            {pagePartial && <PartialChip />}
          </div>
          <p className="max-w-[70ch] text-[13px] leading-relaxed text-[color:var(--c-text-body)]">
            Scoped reference for <span className="text-[color:var(--c-gray-300)]">{ref?.risk_object ?? 'onshore institutional spot trading'}</span>. It does not affect any asset's score.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 md:shrink-0 md:flex-nowrap md:gap-5">
        <div className="text-left font-mono text-[12px] leading-relaxed text-[color:var(--c-text-body)] md:text-right">
          <div>Last comprehensive review · <span className="text-[color:var(--c-text)]">{ref?.last_comprehensive_review ?? '—'}</span></div>
          <div className={pageStale ? 'text-[color:var(--c-gold-light)]' : ''}>{ref?.binding ?? ''}{ref && pageStale ? ` · ${ref.stale_rows}/${ref.total_rows} rows past ${ref.sla_days}d SLA` : ''}</div>
          <div>{rows.length} jurisdictions · {regions.length} regions</div>
        </div>
          <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="fabric-btn px-3">
            {open ? 'Collapse ▴' : 'Expand ▾'}
          </button>
        </div>
      </div>
      {open && (<>

      {/* Jurisdiction matrix — equal cells, hue = restrictiveness band only (SR-358) */}
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--c-text-strong)]">Jurisdiction matrix</h4>
          <div className="flex flex-wrap gap-3 text-[12px] text-[color:var(--c-text-body)]">
            {BANDS.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5">
                <span className={`h-2.5 w-2.5 rounded-sm border ${BAND_STYLE[b].cell}`} />
                {BAND_STYLE[b].label}
              </span>
            ))}
            <span className="text-[color:var(--c-text-soft)]">· colour = spot-regime restrictiveness band; cells are grouped by band, then alphabetical; cell size carries no meaning</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-12">
          {sorted.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelectedId(r.id)}
              aria-pressed={r.id === selectedId}
              className={`rounded border px-2 py-2 text-left transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--c-gold)] ${BAND_STYLE[r.restrictiveness].cell} ${r.id === selectedId ? 'ring-1 ring-[color:rgb(var(--c-gold-rgb)/0.7)]' : ''}`}
            >
              <div className="font-mono text-[13px] font-semibold text-[color:var(--c-text)]">{r.code}</div>
              <div className={`text-[11px] ${BAND_STYLE[r.restrictiveness].text}`}>{r.restrictiveness}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Table 65% + sticky inspector 35% */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,65fr)_minmax(0,35fr)]">
        <div className="overflow-x-auto rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)]">
          <div className="flex items-center justify-between border-b border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-deep)] px-4 py-2.5">
            <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--c-text-strong)]">Jurisdictions · {rows.length}</span>
            {pageStale && <span className="font-mono text-[11px] text-[color:var(--c-gold-light)]">Table state · stale review — rows past the {ref?.sla_days}d SLA are non-binding</span>}
            {pagePartial && <span className="font-mono text-[11px] text-[color:var(--c-text-strong)]">Primary-source review {ref?.last_comprehensive_review} · {ref?.partial_rows} row(s) partial</span>}
          </div>
          <table className="w-full min-w-[640px] text-[13px] leading-[1.4]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.1em] text-[color:var(--c-text-soft)]">
                <th className="px-4 py-2 font-medium">Jurisdiction</th>
                <th className="px-3 py-2 font-medium">Spot regime</th>
                <th className="px-3 py-2 font-medium">Restrictiveness</th>
                <th className="px-3 py-2 font-medium">Framework</th>
                <th className="px-3 py-2 font-medium">Last change</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={`cursor-pointer border-t border-[color:rgb(var(--c-ink-rgb)/0.05)] align-top hover:bg-[color:rgb(var(--c-ink-rgb)/0.025)] ${r.id === selectedId ? 'bg-[color:rgb(var(--c-gold-rgb)/0.06)]' : ''}`}
                >
                  <td className="px-4 py-2.5 text-[color:var(--c-text)]">
                    <span className="mr-2 font-mono text-[12px] text-[color:var(--c-text-soft)]">{r.code}</span>
                    {r.name}
                    {r.reviewState === 'REVIEW_PARTIAL' && <span className="ml-2 font-mono text-[10.5px] text-[color:var(--c-text-strong)]">· partial</span>}
                  </td>
                  <td className="px-3 py-2.5 text-[color:var(--c-gray-300)]">{r.scopeLabel.replace('Spot regime · ', '')}</td>
                  <td className={`px-3 py-2.5 font-mono text-[12px] ${BAND_STYLE[r.restrictiveness].text}`}>{r.restrictiveness}</td>
                  <td className="px-3 py-2.5 text-[color:var(--c-text-body)]">{r.framework ?? '—'}</td>
                  <td className="px-3 py-2.5 font-mono text-[12px] text-[color:var(--c-text-body)]">{r.lastChanged ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selected && (
          <aside key={selected.id} className="self-start rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-deep)] p-5 lg:sticky lg:top-28">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-[12px] text-[color:var(--c-text-soft)]">{selected.code}</div>
                <h4 className="text-lg font-semibold text-[color:var(--c-gray-100)]">{selected.name}</h4>
              </div>
              {selected.reviewState === 'STALE_REVIEW' && <StaleChip days={selected.reviewAgeDays} sla={ref?.sla_days} />}
              {selected.reviewState === 'REVIEW_PARTIAL' && <PartialChip />}
            </div>
            <dl className="space-y-2.5 text-[13px]">
              {[
                ['Spot regime', selected.scopeLabel],
                ['Restrictiveness', BAND_STYLE[selected.restrictiveness].label],
                ['Scope', ref?.risk_object ?? 'onshore institutional spot trading'],
                ['Framework', selected.frameworkFull || selected.framework || '—'],
                ['Last change', selected.lastChanged ?? '—'],
                ['Pending', selected.pendingLegislation ?? '—'],
                ['Last review', `${selected.lastReviewed ?? '—'}${selected.reviewAgeDays >= 0 ? ` · ${selected.reviewAgeDays} days ago` : ''}`],
                ['Evidence state', selected.reviewState === 'STALE_REVIEW' ? 'Stale review — non-binding until refreshed' : selected.reviewState === 'REVIEW_PARTIAL' ? 'Partial primary-source review — non-binding' : 'Primary-source review within SLA'],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[8.5rem_1fr] gap-3">
                  <dt className="text-[color:var(--c-text-soft)]">{k}</dt>
                  <dd className="text-[color:var(--c-gray-300)]">{v}</dd>
                </div>
              ))}
              <div className="grid grid-cols-[8.5rem_1fr] gap-3">
                <dt className="text-[color:var(--c-text-soft)]">Primary sources</dt>
                <dd className="space-y-2">
                  {/* SR-367: only evidence scoped to this jurisdiction (or declared GLOBAL); unique keys so a previous
                      selection's nodes can never persist. */}
                  {(selected.sources ?? []).filter((src) => !src.jurisdiction || src.jurisdiction === selected.id || src.jurisdiction === 'GLOBAL').map((src, i) => (
                    <div key={`${selected.id}-${i}-${src.url}`} className="text-[12px]">
                      {src.jurisdiction === 'GLOBAL' && <span className="mr-1 font-mono text-[10.5px] text-[color:var(--c-text-soft)]">GLOBAL ·</span>}
                      <a href={src.url} target="_blank" rel="noopener noreferrer" className="break-all text-[color:var(--c-gold)] hover:underline">{src.publisher || src.url.replace(/^https?:\/\//, '').split('/')[0]}</a>
                      {src.quote && <div className="mt-0.5 italic text-[color:var(--c-text-body)]">"{src.quote.length > 220 ? `${src.quote.slice(0, 220)}…` : src.quote}"</div>}
                      {src.sha256 && <div className="font-mono text-[10.5px] text-[color:var(--c-text-soft)]">capture sha256 {src.sha256.slice(0, 12)}…</div>}
                    </div>
                  ))}
                  {(selected.sources ?? []).length === 0 && (selected.sourceUrl ? (
                    <a href={selected.sourceUrl} target="_blank" rel="noopener noreferrer" className="break-all text-[color:var(--c-gold)] hover:underline">
                      {selected.sourceUrl.replace(/^https?:\/\//, '')}
                    </a>
                  ) : (
                    <span className="text-[color:var(--c-text-body)]">no primary source on file</span>
                  ))}
                </dd>
              </div>
            </dl>
            {(selected.claims ?? []).length > 0 && (
              <div className="mt-4 border-t border-[color:rgb(var(--c-ink-rgb)/0.06)] pt-3">
                <div className="mb-2 text-[11px] uppercase tracking-[0.12em] text-[color:var(--c-text-soft)]">Material claims · each primary-verified separately</div>
                <ul className="space-y-1.5 text-[12px]">
                  {(selected.claims ?? []).map((c) => (
                    <li key={`${selected.id}-${c.claim_id}`} className="grid grid-cols-[auto_1fr] gap-2">
                      <span className={`font-mono text-[10.5px] ${c.verdict === 'VERIFIED' ? 'text-[color:var(--c-hex-a3b07a)]' : 'text-[color:var(--c-amber)]'}`}>{c.verdict}</span>
                      <span className="text-[color:var(--c-hex-c9d0d8)]">{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{c.proposition}</a> : c.proposition}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <details className="mt-4 border-t border-[color:rgb(var(--c-ink-rgb)/0.06)] pt-3 text-[12px] text-[color:var(--c-text-soft)]">
              <summary className="cursor-pointer select-none text-[color:var(--c-text-body)]">Reviewer detail</summary>
              <p className="mt-2">
                Heuristic restrictiveness reference {selected.riskScore}/100 — a categorical policy mapping, not a calibrated risk estimate; differences within a band are not meaningful.
              </p>
            </details>
          </aside>
        )}
      </div>

      {/* Regions — composition, never a worst-of verdict (SR-351 / SR-354) */}
      {regions.length > 0 && (
        <div>
          <h4 className="mb-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--c-text-strong)]">Regions · derived from the jurisdiction rows</h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {regions.map((g) => {
              const partial = g.regionState === 'PARTIAL_COVERAGE';
              const single = g.regionState === 'JURISDICTION';
              const headline = single ? g.members[0]?.statusLabel ?? '—' : partial ? 'Partial coverage' : g.regionState === 'MIXED' ? 'Mixed' : g.members[0]?.statusLabel ?? '—';
              return (
                <div key={g.code} className="rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-deep)] px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[13px] font-semibold text-[color:var(--c-text)]">{g.name}</div>
                      <div className={`mt-0.5 text-[13px] ${partial ? 'text-[color:var(--c-gold-light)]' : 'text-[color:var(--c-gray-300)]'}`}>{headline}</div>
                    </div>
                    {g.reviewState === 'STALE_REVIEW' && <span className="font-mono text-[10.5px] text-[color:var(--c-gold-light)]">STALE</span>}
                    {g.reviewState === 'REVIEW_PARTIAL' && <span className="font-mono text-[10.5px] text-[color:var(--c-text-strong)]">PARTIAL REVIEW</span>}
                  </div>
                  <div className="mt-2 text-[12px] text-[color:var(--c-text-body)]">
                    {single && 'Jurisdiction reference'}
                    {partial && `${g.composition.total} jurisdiction reviewed — ${g.members.map((m) => `${m.name}: ${m.statusLabel.replace('Spot regime · ', '')}`).join('; ')}. No regional state is issued.`}
                    {!single && !partial && compositionText(g.composition)}
                  </div>
                  {!single && !partial && g.screeningWorst && (
                    <div className="mt-1.5 font-mono text-[11px] text-[color:var(--c-text-soft)]" title="Conservative screening statistic — not the representative regional state.">
                      Worst-case member (screening): {g.screeningWorst}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>)}
    </div>
  );
};

export default GlobalRegulatoryReference;
